import { css } from '@emotion/react';
import { type Theme } from '../../../themes/theme.model';
import { type InputSize } from './textInput.component';
import {
  getTextInputHeight, getTextInputIconFontSize,
} from './textInput.helpers';

export const getInputCommonColors = (theme: Theme, isError = false) => {
  const borderColorError = theme.borderColors.error;

  return ({
    backgroundColor: theme.backgroundColors.secondary,
    backgroundColorActive: theme.backgroundColors.highlight,
    borderColor: isError ? borderColorError : theme.borderColors.primary,
    borderColorActive: isError ? borderColorError : theme.borderColors.activeItem,
    color: theme.textColors.primary,
  });
};

export const inputCommonStyle = (theme: Theme, isFocusable: boolean, isError = false) => {
  const { backgroundColorActive, borderColorActive, borderColor, backgroundColor, color } = getInputCommonColors(theme, isError);
  return css({
    backgroundColor,
    border: `1px solid ${borderColor}`,
    borderRadius: 4,
    color,
    overflow: 'hidden',

    '&:focus-within': isFocusable ? {
      background: backgroundColorActive,
      borderColor: borderColorActive,
    } : {},
  });
};

export const wrapperStyle = ({ size, isDisabled, isReadOnly, isError }: Readonly<{
  size: InputSize;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  isError?: boolean;
}>) => (theme: Theme) => css(inputCommonStyle(theme, !(isReadOnly || isDisabled), isError), {
  alignItems: 'center',
  boxSizing: 'border-box',
  cursor: isDisabled ? 'default' : 'text',
  display: 'flex',
  fontFamily: 'inherit',
  fontSize: 16,
  fontWeight: 500,
  height: getTextInputHeight(size),
  opacity: isDisabled ? 0.5 : 1,
  padding: '0 8px',
  width: '100%',

  ...isReadOnly ? {
    color: theme.textColors.secondary,
    cursor: 'default',
  } : {},
});

export const inputStyles = (theme: Theme) => css({
  color: 'inherit',
  outline: 'none',
  border: 'none',
  minWidth: 0,
  width: '100%',
  flexGrow: 100,
  backgroundColor: 'transparent',
  fontSize: 'inherit',
  fontWeight: 'inherit',
  '::placeholder': {
    opacity: .4,
    color: 'inherit',
  },
  padding: 0,

  '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
    WebkitTextFillColor: theme.textColors.primary,
    WebkitBoxShadow: `0 0 0px 1000px ${theme.backgroundColors.secondary}  inset`,
    transition: 'background-color 5000s ease-in-out 0s',
  },
});

export const getIconStyles = (size: InputSize) => (theme: Theme) => css({
  color: theme.iconColors.secondary,
  display: 'inline-flex',
  fontSize: getTextInputIconFontSize(size),
  padding: '0 6px 0 4px',
});

export const showHideIconContainerStyle = ({ size }: { size: InputSize }) => css({
  fontSize: getTextInputIconFontSize(size),
  minWidth: 36,
  display: 'flex',
  height: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
});

export const passwordMaskedStyle = css({
  WebkitTextSecurity: 'disc',
});

export const additionalContentStyle = css({
  alignItems: 'center',
  display: 'flex',
  height: '100%',
  flex: '0 1 auto',
});
